import { FC, useState } from 'react';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import lang from '../../lib/language';
import { EpicMoversTabEnum } from '../../lib/models/fundamental/types';
import { isEmpty } from '../../lib/util/typeGuards';

import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import DiscoverButton from '../DiscoverButton/DiscoverButton';
import FilterDropDown from '../FilterDropDown/FilterDropDown';
import useIsMobile from '../hooks/useIsMobile';
import NoDataAvailable from '../NoDataAvailable/NoDataAvailable';

import EpicMoversList from './components/EpicMoversList/EpicMoversList';
import { EpicMoversProps } from './EpicMovers.types';

import styles from './EpicMovers.module.scss';

const EpicMovers: FC<EpicMoversProps> = ({
  topGainers,
  topLosers,
  isPending,
  isReady,
  filterOptionValue,
  filterCurrencySymbol = CurrencySymbolEnum.USD,
  onFilterChange,
  onViewAllClick,
  onSymbolClick,
}: EpicMoversProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const [selectedTab, setSelectedTab] = useState<EpicMoversTabEnum>(EpicMoversTabEnum.TopGainers);
  const isTopGainersSelected = selectedTab === EpicMoversTabEnum.TopGainers;
  const isTopLoserSelected = selectedTab === EpicMoversTabEnum.TopLosers;

  const hasNoAnyData = isEmpty(topGainers) && isEmpty(topLosers) && !isPending;

  const filterDropDown = (
    <FilterDropDown
      isDisabled={isPending}
      optionValue={filterOptionValue}
      handleFilterOption={onFilterChange}
      currencySymbol={filterCurrencySymbol as CurrencySymbolEnum}
    />
  );

  return (
    <>
      <div className={styles.titleWrapper}>
        <div className={styles.titleRow}>
          <div>
            <div className={GlobalClassesEnum.CommonDiscoverTitle}>
              {lang.commonEpicMoversToday()}
            </div>
          </div>
          <div className={styles.titleActions}>
            {!isMobile && filterDropDown}
            {!hasNoAnyData && (
              isMobile ? (
                <Button
                  variant={ButtonTypesEnum.Link}
                  onClick={() => onViewAllClick(isTopGainersSelected)}
                  className={styles.viewAll}
                >
                  {lang.commonViewAll()}
                </Button>
              ) : (
                <DiscoverButton
                  disabled={isPending}
                  onClick={() => onViewAllClick(isTopGainersSelected)}
                />
              )
            )}
          </div>
        </div>
        {isMobile && (
          <div className={styles.tabsWrapper}>
            <div className={styles.tabs}>
              <Button
                className={isTopGainersSelected ? styles.tabActive : styles.tab}
                onClick={() => setSelectedTab(EpicMoversTabEnum.TopGainers)}
              >
                {lang.commonEpicMoversGainers()}
              </Button>

              <Button
                className={isTopLoserSelected ? styles.tabActive : styles.tab}
                onClick={() => setSelectedTab(EpicMoversTabEnum.TopLosers)}
              >
                {lang.commonEpicMoversLosers()}
              </Button>
            </div>
            {filterDropDown}
          </div>
        )}
      </div>
      <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
        <div className={styles.listWrapper}>
          <div className={styles.list}>
            {hasNoAnyData ? (
              <NoDataAvailable
                className={styles.noDataToDisplay}
                isNoDataIcon
                title={lang.commonDiscover52WeekNoDataAll()}
              />
            ) : (
              <>
                {(!isMobile || isTopGainersSelected) && (
                  <EpicMoversList
                    isReduction={false}
                    title={lang.commonEpicMoversGainers()}
                    data={topGainers}
                    isPending={isPending}
                    isReady={isReady}
                    onSymbolClick={symbol => onSymbolClick(symbol, true)}
                  />
                )}
                {!isMobile && <div className={styles.verticalSpace} />}
                {(!isMobile || isTopLoserSelected) && (
                  <EpicMoversList
                    isReduction
                    title={lang.commonEpicMoversLosers()}
                    data={topLosers}
                    isPending={isPending}
                    isReady={isReady}
                    onSymbolClick={symbol => onSymbolClick(symbol, false)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EpicMovers;
